/* MALVICE collection catalog. Fetch every collection page before filtering,
   so a category never filters only the first page of products. */
(() => {
  if (customElements.get('malvice-catalog')) return;
  const normalize = value => String(value || '').normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '').replace(/['’‘]/g, '').toLowerCase();
  const types = {
    all: null,
    't-shirts': ['t-shirt', 't-shirts', 'tee', 'tees'],
    hoodies: ['hoodie', 'hoodies'],
    sweatshirts: ['sweatshirt', 'sweatshirts'],
    caps: ['cap', 'caps', 'dad-hat', 'baseball-cap'],
    'crop-tees': ['crop-top', 'crop-tops', 'crop-tee', 'crop-tees']
  };

  class MalviceCatalog extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.setupTimer = setTimeout(() => this.setup(), 0);
    }
    disconnectedCallback() {
      clearTimeout(this.setupTimer);
      clearTimeout(this.searchTimer);
      cancelAnimationFrame(this.autoLoadFrame);
      this.scrollObserver?.disconnect();
      this.events?.abort();
      this.requests?.abort();
      this.initialized = false;
    }
    setup() {
      if (!this.isConnected || this.initialized) return;
      this.grid = this.querySelector('[data-catalog-grid]');
      if (!this.grid) return;
      this.initialized = true;
      this.events = new AbortController();
      this.requests = new AbortController();
      this.input = this.querySelector('[data-catalog-search]');
      this.count = this.querySelector('[data-catalog-count]');
      this.status = this.querySelector('[data-catalog-status]');
      this.empty = this.querySelector('[data-catalog-empty]');
      this.error = this.querySelector('[data-catalog-error]');
      this.more = this.querySelector('[data-catalog-more]');
      this.shown = this.querySelector('[data-catalog-shown]');
      this.sort = this.querySelector('[data-catalog-sort]');
      this.clear = this.querySelector('[data-catalog-clear]');
      this.buttons = [...this.querySelectorAll('[data-catalog-type]')];
      this.filterMode = this.dataset.filterMode === 'gender' ? 'gender' : 'type';
      this.hashKey = this.filterMode === 'gender' ? 'catalog-gender' : 'catalog-type';
      this.cards = new Map();
      this.addCards(this.grid);
      this.pages = Math.max(1, Number(this.dataset.pages) || 1);
      this.loadedPages = new Set([Number(this.dataset.currentPage) || 1]);
      this.complete = this.pages === 1;
      this.total = Number(this.dataset.total) || 0;
      this.limit = Number(this.dataset.pageSize) || 24;
      this.step = this.limit;
      this.state = this.readState();
      this.input.value = this.state.search;
      this.revision = 0;
      this.loading = null;
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => this.onClick(event), options);
      this.input.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          this.state.search = this.input.value.trim();
          this.limit = this.step;
          this.writeState(false);
          this.update();
        }, 180);
      }, options);
      this.querySelector('[data-catalog-form]').addEventListener('submit', event => {
        event.preventDefault();
        clearTimeout(this.searchTimer);
        this.state.search = this.input.value.trim();
        this.limit = this.step;
        this.writeState(false);
        this.update();
      }, options);
      this.sort.addEventListener('change', () => {
        clearTimeout(this.searchTimer);
        this.state.search = this.input.value.trim();
        this.state.sort = this.sort.value;
        this.limit = this.step;
        this.writeState(true);
        this.update();
      }, options);
      const restore = () => {
        clearTimeout(this.searchTimer);
        this.state = this.readState();
        this.input.value = this.state.search;
        this.limit = this.step;
        this.update();
      };
      window.addEventListener('popstate', restore, options);
      window.addEventListener('hashchange', restore, options);
      this.dataset.enhanced = 'true';
      this.buttons.forEach(button => { button.disabled = false; });
      this.sort.disabled = false;
      this.querySelector('[data-catalog-pagination]')?.setAttribute('hidden', '');
      this.syncControls();
      this.setupInfiniteScroll();
      if (this.complete) this.render();
      else if (this.state.type !== 'all' || this.state.search || this.state.sort !== 'featured' || Number(this.dataset.currentPage) > 1) this.update();
      else {
        this.syncMoreControl(this.cards.size < this.total);
        this.shown.textContent = `Showing ${this.cards.size} of ${this.total} ${this.total === 1 ? 'style' : 'styles'}`;
      }
    }
    readState() {
      const params = new URLSearchParams(location.hash.replace(/^#/, ''));
      const requested = params.get(this.hashKey) || 'all';
      const type = this.buttons.some(button => button.dataset.catalogType === requested) ? requested : 'all';
      const requestedSort = params.get('catalog-sort') || 'featured';
      const sort = [...this.sort.options].some(option => option.value === requestedSort) ? requestedSort : 'featured';
      return { type, search: (params.get('catalog-search') || '').slice(0, 160), sort };
    }
    writeState(push) {
      const url = new URL(location.href);
      const params = new URLSearchParams();
      if (this.state.type !== 'all') params.set(this.hashKey, this.state.type);
      if (this.state.search) params.set('catalog-search', this.state.search);
      if (this.state.sort !== 'featured') params.set('catalog-sort', this.state.sort);
      url.hash = params.toString();
      if (url.href !== location.href) {
        try { history[push ? 'pushState' : 'replaceState'](history.state, '', url); } catch (_) { /* Sandboxed previews can disallow history. */ }
      }
    }
    setupInfiniteScroll() {
      this.infiniteScroll = 'IntersectionObserver' in window && this.more instanceof HTMLElement;
      if (!this.infiniteScroll) return;
      this.sentinel = this.more.parentElement;
      if (!(this.sentinel instanceof HTMLElement)) {
        this.infiniteScroll = false;
        return;
      }
      this.sentinel.style.minHeight = '1px';
      this.scrollObserver = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.target === this.sentinel && entry.isIntersecting)) {
            this.maybeLoadMore();
          }
        },
        { rootMargin: '600px 0px', threshold: 0 }
      );
      this.scrollObserver.observe(this.sentinel);
    }
    scheduleInfiniteScroll() {
      if (!this.infiniteScroll || !this.hasMore) return;
      cancelAnimationFrame(this.autoLoadFrame);
      this.autoLoadFrame = requestAnimationFrame(() => this.maybeLoadMore());
    }
    maybeLoadMore() {
      if (
        !this.isConnected ||
        !this.infiniteScroll ||
        !this.hasMore ||
        this.autoLoading ||
        this.loading ||
        this.getAttribute('aria-busy') === 'true' ||
        !this.error.hidden
      ) return;
      const bounds = this.sentinel.getBoundingClientRect();
      if (bounds.top > window.innerHeight + 600 || bounds.bottom < -600) return;
      this.autoLoading = true;
      this.limit += this.step;
      this.update().finally(() => {
        this.autoLoading = false;
        this.scheduleInfiniteScroll();
      });
    }
    syncMoreControl(hasMore) {
      this.hasMore = hasMore;
      this.more.hidden = this.infiniteScroll || !hasMore;
      this.scheduleInfiniteScroll();
    }
    syncControls() {
      this.buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.catalogType === this.state.type)));
      this.sort.value = this.state.sort;
      this.clear.hidden = this.input.value.length === 0;
    }
    onClick(event) {
      const control = event.target.closest('[data-catalog-type], [data-catalog-clear], [data-catalog-reset], [data-catalog-more], [data-catalog-retry]');
      if (!control || !this.contains(control)) return;
      event.preventDefault();
      clearTimeout(this.searchTimer);
      this.state.search = this.input.value.trim();
      this.focusNewFrom = null;
      if (control.hasAttribute('data-catalog-type')) {
        this.state.type = control.dataset.catalogType;
        this.limit = this.step;
        this.writeState(true);
      } else if (control.hasAttribute('data-catalog-clear')) {
        this.state.search = '';
        this.input.value = '';
        this.limit = this.step;
        this.writeState(false);
        this.input.focus();
      } else if (control.hasAttribute('data-catalog-reset')) {
        this.state = { type: 'all', search: '', sort: 'featured' };
        this.input.value = '';
        this.limit = this.step;
        this.writeState(true);
      } else if (control.hasAttribute('data-catalog-more')) {
        this.focusNewFrom = this.limit;
        this.limit += this.step;
      }
      this.update();
    }
    addCards(root) {
      root.querySelectorAll('[data-catalog-card]').forEach(node => {
        if (!this.cards.has(node.dataset.productId)) {
          this.cards.set(node.dataset.productId, {
            node,
            type: node.dataset.kind,
            genders: (node.dataset.genders || '').split(',').map(value => normalize(value).trim()).filter(Boolean),
            title: normalize(node.dataset.title || node.dataset.search),
            text: normalize(node.dataset.search),
            index: Number(node.dataset.index) || 0,
            price: Number(node.dataset.price) || 0,
            created: Number(node.dataset.created) || 0
          });
        }
      });
    }
    async fetchPage(page) {
      const url = new URL(location.href);
      url.hash = '';
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set(this.dataset.pageParam || 'page', String(page));
      const controller = new AbortController();
      const abort = () => controller.abort();
      this.requests.signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 15000);
      try {
        const response = await fetch(url, {
          credentials: 'same-origin', signal: controller.signal,
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`Catalog request failed (${response.status})`);
        const html = await response.text();
        const documentCopy = new DOMParser().parseFromString(html, 'text/html');
        const catalog = [...documentCopy.querySelectorAll('malvice-catalog')]
          .find(element => element.dataset.sectionId === this.dataset.sectionId && element.dataset.collectionId === this.dataset.collectionId && element.dataset.collectionHandle === this.dataset.collectionHandle);
        if (!catalog || Number(catalog.dataset.currentPage) !== page) throw new Error('Unexpected catalog response');
        if (this.requests.signal.aborted || !this.isConnected) throw new Error('Canceled');
        this.addCards(catalog);
        this.loadedPages.add(page);
      } finally {
        clearTimeout(timeout);
        this.requests.signal.removeEventListener('abort', abort);
      }
    }
    async loadAll() {
      if (this.complete) return;
      if (this.loading) return this.loading;
      const queue = Array.from({ length: this.pages }, (_, index) => index + 1)
        .filter(page => !this.loadedPages.has(page));
      this.loading = (async () => {
        const errors = [];
        const worker = async () => {
          while (queue.length && !this.requests.signal.aborted) {
            const page = queue.shift();
            try { await this.fetchPage(page); } catch (error) { errors.push(error); }
          }
        };
        await Promise.all([worker(), worker()]);
        if (errors.length || this.requests.signal.aborted) throw errors[0] || new Error('Canceled');
        if (this.loadedPages.size !== this.pages || this.cards.size !== this.total) {
          throw new Error('Collection changed while loading. Refresh the page before retrying.');
        }
        this.complete = true;
        this.total = this.cards.size;
      })();
      try { await this.loading; } finally { this.loading = null; }
    }
    async update() {
      const revision = ++this.revision;
      this.syncControls();
      this.error.replaceChildren();
      this.error.hidden = true;
      this.setAttribute('aria-busy', 'true');
      this.more.disabled = true;
      if (!this.complete) {
        this.count.textContent = 'Loading styles…';
        this.status.textContent = 'Loading the full collection.';
      }
      try {
        await this.loadAll();
        if (!this.isConnected || revision !== this.revision) return;
        this.render();
      } catch (error) {
        if (!this.isConnected || revision !== this.revision) return;
        const message = document.createElement('p');
        message.textContent = this.error.dataset.catalogErrorMessage;
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'mvc-action';
        retry.dataset.catalogRetry = '';
        retry.textContent = this.error.dataset.catalogRetryLabel;
        this.error.replaceChildren(message, retry);
        this.error.hidden = false;
        this.syncMoreControl(false);
        this.count.textContent = 'Please retry loading styles';
        this.status.textContent = 'Unable to load the full collection. Your previous results are still shown. Please retry, or refresh this page if products have changed.';
        this.querySelector('[data-catalog-pagination]')?.removeAttribute('hidden');
      } finally {
        if (this.isConnected && revision === this.revision) {
          this.setAttribute('aria-busy', 'false');
          this.more.disabled = false;
        }
      }
    }
    matchesFilter(card, selected) {
      if (selected === 'all') return true;
      if (this.filterMode === 'gender') {
        if (selected === 'unisex') return card.genders.includes('unisex');
        if (selected === 'men') return card.genders.includes('unisex') || card.genders.includes('male');
        if (selected === 'women') return card.genders.includes('unisex') || card.genders.includes('female');
        return false;
      }
      const accepted = types[selected] || [selected];
      return accepted.includes(card.type);
    }
    render() {
      const words = normalize(this.state.search).split(/\s+/).filter(Boolean);
      const allCards = [...this.cards.values()];
      const matches = allCards.filter(card =>
        this.matchesFilter(card, this.state.type) && words.every(word => card.text.includes(word))
      );
      // Hide unavailable audience/type filters only after the complete collection is known.
      if (this.complete) this.buttons.forEach(button => {
        const selected = button.dataset.catalogType;
        button.hidden = selected !== 'all' && selected !== this.state.type && !allCards.some(card => this.matchesFilter(card, selected));
      });
      const byIndex = (a, b) => a.index - b.index;
      const comparators = {
        featured: byIndex,
        newest: (a, b) => b.created - a.created || byIndex(a, b),
        'price-ascending': (a, b) => a.price - b.price || byIndex(a, b),
        'price-descending': (a, b) => b.price - a.price || byIndex(a, b),
        'title-ascending': (a, b) => a.title.localeCompare(b.title) || byIndex(a, b)
      };
      matches.sort(comparators[this.state.sort] || byIndex);
      const visible = matches.slice(0, this.limit);
      const fragment = document.createDocumentFragment();
      visible.forEach(card => fragment.append(card.node));
      this.grid.replaceChildren(fragment);
      if (Number.isInteger(this.focusNewFrom)) {
        visible[this.focusNewFrom]?.node.querySelector('a[href],button:not([disabled])')?.focus({ preventScroll: true });
        this.focusNewFrom = null;
      }
      this.querySelector('[data-catalog-pagination]')?.setAttribute('hidden', '');
      this.empty.hidden = matches.length !== 0;
      this.syncMoreControl(visible.length < matches.length);
      this.shown.textContent = `Showing ${visible.length} of ${matches.length} ${matches.length === 1 ? 'style' : 'styles'}`;
      this.count.textContent = `${matches.length} ${matches.length === 1 ? 'style' : 'styles'} ${words.length ? 'found' : 'available'}`;
      this.status.textContent = `${matches.length} matching ${matches.length === 1 ? 'style' : 'styles'}. Showing ${visible.length}.`;
      this.dispatchEvent(new CustomEvent('malvice:catalog-updated', { bubbles: true, detail: { count: matches.length, type: this.state.type } }));
    }
  }
  customElements.define('malvice-catalog', MalviceCatalog);
})();
